import React, {useState, useMemo, useCallback} from "react";
import PropTypes from "prop-types";
import {FormattedMessage} from "react-intl";
import {connect} from "react-redux";
import {PropagateLoader} from 'react-spinners';
import {Link} from "react-router-dom";

// @mui/material components
import Grid from "@mui/material/Grid";

// utils
import {MarketplaceRoutes} from "utils/MarketplaceRoutes";

// local components
import RetailerListHeader from "./components/RetailerListHeader";
import RetailerItemLine from "./components/RetailerItemLine";

// style
import {primaryColor} from "assets/jss/main";

const SORTABLE_COLUMNS = {
    name: {property: 'name', type: 'string'},
    validProductCount: {property: 'validProductCount', type: 'number'},
    unmappedProductCount: {property: 'unmappedProductCount', type: 'number'},
    incompleteProductCount: {property: 'incompleteProductCount', type: 'number'},
};

function sortData(data, sortKey, sortDirection) {
    if (!sortKey || !data || !SORTABLE_COLUMNS[sortKey]) return data;
    const {property, type} = SORTABLE_COLUMNS[sortKey];

    return [...data].sort((a, b) => {
        let valA = a[property];
        let valB = b[property];

        if (valA == null) return 1;
        if (valB == null) return -1;

        if (type === 'string') {
            valA = valA.toLowerCase();
            valB = valB.toLowerCase();
        }

        if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });
}

function TableDemo({tableData, match, del, createStripe, loading, deleteLoading, authorizations, stripeLoading}) {
    const [sortKey, setSortKey] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');

    const handleSort = useCallback((key) => {
        if (sortKey === key) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDirection('asc');
        }
    }, [sortKey]);

    const sortedData = useMemo(
        () => sortData(tableData, sortKey, sortDirection),
        [tableData, sortKey, sortDirection]
    );

    return (
        <div>
            <div style={{position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#fff'}}>
                <RetailerListHeader
                    sortKey={sortKey}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                />
            </div>
            {!tableData ?
                <Grid container justifyContent={"center"} sx={{padding: '40px 0'}}>
                    <PropagateLoader
                        sizeUnit={"px"}
                        size={16}
                        color={primaryColor}
                        loading={loading || deleteLoading}
                    />
                </Grid>
                :
                sortedData.length > 0 ?
                    <Grid container direction={"column"} sx={{gap: '8px'}}>
                        {sortedData.map((item, index) => (
                            <Link
                                key={item.id}
                                to={MarketplaceRoutes.RETAILER_SHOW(item.id)}
                                style={{textDecoration: 'none', color: 'inherit', display: 'block'}}
                            >
                                <RetailerItemLine
                                    retailer={item}
                                    index={index}
                                    match={match}
                                    del={del}
                                    createStripe={createStripe}
                                    authorizations={authorizations}
                                    stripeLoading={stripeLoading}
                                />
                            </Link>
                        ))}
                    </Grid>
                    :
                    <Grid container justifyContent={"center"} sx={{padding: '40px 0'}}>
                        <FormattedMessage id={"retailer.list.table.no_result"} />
                    </Grid>
            }
        </div>
    );
}

TableDemo.propTypes = {
    tableData: PropTypes.array,
    match: PropTypes.object,
    del: PropTypes.func,
    createStripe: PropTypes.func,
    loading: PropTypes.bool,
    deleteLoading: PropTypes.bool,
    authorizations: PropTypes.array,
    stripeLoading: PropTypes.bool,
};

const mapStateToProps = state => ({
    deleteLoading: state.retailer.del.loading,
    loading: state.retailer.list.loading,
    authorizations: state.authentication.authorizations,
    stripeLoading: state.retailer.create.loading,
});

export default connect(mapStateToProps)(TableDemo);
